<script setup lang="ts">
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import NavHeader from '@/components/menu/NavHeader.vue';
import { useRoute } from 'vue-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { onMounted, onUnmounted, reactive, useTemplateRef } from 'vue';
import { router, ROUTES } from '@/router.ts';
import {
    eventEmitter,
    type HotkeyPressPayload,
    type ScrollInDirectionPayload,
} from '@/lib/eventEmitter.ts';
import { Hotkey } from '@/types/Hotkey.ts';
import { getElementViewportPosition } from '@/utils/ViewPortUtils.ts';
import OutputCard from '@/components/output/OutputCard.vue';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import { z } from 'zod';
import type { StepConfig } from '@/types/edit/StepConfig.ts';
import HotkeyIndicator from '@/components/hotkey/HotkeyIndicator.vue';
import { useClientStore } from '@/stores/ClientStore.ts';
import { getNeighbouringStepConfig } from '@/utils/PromptUtils.ts';
import { logger } from '@/lib/logger.ts';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import { PromptConfigService } from '@/services/config/PromptConfigService.ts';
import { ProfileConfigService } from '@/services/config/ProfileConfigService.ts';
import { StepConfigService } from '@/services/config/StepConfigService.ts';
import { VariableService } from '@/services/VariableService.ts';
import type { SessionId } from '@/types/Session.ts';
import { syncToWebViewAction } from '@/actions/sync/SyncToWebViewAction.ts';
import { SYNC_TO_WEB_VIEW_KEYS } from '@/types/sync/SyncKeys.ts';

/* region Router */
const route = useRoute();
const routeParamsSchema = z.object({
    sessionId: z.string(),
    profileConfigId: z.string(),
    stepConfigId: z.string(),
    variableChainHash: z.string(),
});
/* endregion Router */

/* region Setup */
const clientStore = useClientStore();
const sessionStore = useSessionStore();

const runPageBody = useTemplateRef<HTMLDivElement>('runPageBody');
/* endregion Setup */

/* region State */
interface ComponentState {
    sessionId: string | undefined;
    profileConfig: ProfileConfig | undefined;
    profileStepConfigs: StepConfig[];
    stepConfig: StepConfig | undefined;
    promptConfigs: PromptConfig[];
    variableChainHash: string;
    lastScrollTime: number | undefined;
}

const state: ComponentState = reactive({
    sessionId: undefined,
    profileConfig: undefined,
    profileStepConfigs: [],
    stepConfig: undefined,
    promptConfigs: [],
    variableChainHash: '_',
    lastScrollTime: undefined,
});
/* endregion State */

/* region Event handlers */
const onHotkeyPress = (payload: HotkeyPressPayload) => {
    if (!state.sessionId || !state.profileConfig || !state.stepConfig) {
        return;
    }

    const hotkey = payload.hotkey;
    const navigationId = `${state.sessionId}_${state.stepConfig.id}`;

    const curSelection = clientStore.verticalNavigationSelection[navigationId] ?? 0;
    let options: number[] = [];

    const stepHasChoicePrompts =
        state.promptConfigs.filter((p) => {
            return p.formatAs === 'choice';
        }).length > 0;

    if (clientStore.verticalNavigationOptions[navigationId] !== undefined) {
        options = Object.keys(clientStore.verticalNavigationOptions[navigationId])
            .map((key) => Number(key))
            .sort((a, b) => a - b);
    }

    if (hotkey === Hotkey.Up || hotkey === Hotkey.Down) {
        // UP
        if (hotkey === Hotkey.Up) {
            if (stepHasChoicePrompts) {
                for (let i = options.length; i >= 0; i--) {
                    if (options[i] < curSelection) {
                        clientStore.verticalNavigationSelection[navigationId] = options[i];
                        break;
                    } else {
                        scrollInDirection('up');
                        syncToWebViewAction(
                            SYNC_TO_WEB_VIEW_KEYS.scroll,
                            JSON.stringify({
                                direction: 'up',
                            }),
                        );
                    }
                }
            } else {
                scrollInDirection('up');
                syncToWebViewAction(
                    SYNC_TO_WEB_VIEW_KEYS.scroll,
                    JSON.stringify({
                        direction: 'up',
                    }),
                );
            }
        }

        // DOWN
        if (hotkey === Hotkey.Down) {
            if (stepHasChoicePrompts) {
                for (let i = 0; i < options.length; i++) {
                    if (options[i] > curSelection) {
                        clientStore.verticalNavigationSelection[navigationId] = options[i];
                        break;
                    } else {
                        scrollInDirection('down');
                        syncToWebViewAction(
                            SYNC_TO_WEB_VIEW_KEYS.scroll,
                            JSON.stringify({
                                direction: 'down',
                            }),
                        );
                    }
                }
            } else {
                scrollInDirection('down');
                syncToWebViewAction(
                    SYNC_TO_WEB_VIEW_KEYS.scroll,
                    JSON.stringify({
                        direction: 'down',
                    }),
                );
            }
        }

        const newSelection = clientStore.verticalNavigationSelection[navigationId];
        const divElement = document.getElementById(`choice-${newSelection}`);

        if (divElement && runPageBody.value) {
            const position = getElementViewportPosition(divElement, runPageBody.value);

            if (position === 'below') {
                runPageBody.value.scrollBy(0, runPageBody.value.scrollHeight / 2);
            } else if (position === 'above') {
                runPageBody.value.scrollBy(0, -(runPageBody.value.scrollHeight / 2));
            }
        }
    }

    // LEFT
    if (hotkey === Hotkey.Left) {
        if (state.stepConfig?.order === 0) {
            router.push({
                name: ROUTES.index,
            });
        } else {
            const previousStepConfig = getNeighbouringStepConfig(
                state.profileConfig?.id,
                state.stepConfig?.id,
                'previous',
            );

            if (!previousStepConfig) {
                logger.debug('No step config found for previous step');

                return;
            }

            router.push({
                name: ROUTES.runIndex,
                params: {
                    sessionId: state.sessionId,
                    profileConfigId: state.profileConfig?.id,
                    stepConfigId: previousStepConfig.id,
                    variableChainHash: calculateVariableChainHash(
                        state.sessionId,
                        previousStepConfig.id,
                    ),
                },
            });
        }
    }

    // RIGHT
    if (hotkey === Hotkey.Right) {
        const nextStepConfig = getNeighbouringStepConfig(
            state.profileConfig.id,
            state.stepConfig.id,
            'next',
        );

        if (!nextStepConfig) {
            logger.debug('No step config found for next step');

            return;
        }

        if (!stepHasChoicePrompts) {
            void router.push({
                name: ROUTES.runIndex,
                params: {
                    sessionId: state.sessionId,
                    profileConfigId: state.profileConfig.id,
                    stepConfigId: nextStepConfig.id,
                    variableChainHash: calculateVariableChainHash(
                        state.sessionId,
                        nextStepConfig.id,
                    ),
                },
            });
        }
    }
};

const onScrollInDirection = (payload: ScrollInDirectionPayload) => {
    scrollInDirection(payload.direction);
};
/* endregion Event handlers */

/* region Other */
const calculateVariableChainHash = (
    sessionId: SessionId,
    stepConfigId: StepConfig['id'],
): string => {
    return VariableService.calculateVariableChainHash(sessionId, stepConfigId);
};

const navigateToRunPage = async (
    sessionId: SessionId,
    profileConfigId: ProfileConfig['id'],
    stepConfigId: StepConfig['id'],
) => {
    void router.push({
        name: ROUTES.runIndex,
        params: {
            sessionId: sessionId,
            profileConfigId: profileConfigId,
            stepConfigId: stepConfigId,
            variableChainHash: calculateVariableChainHash(sessionId, stepConfigId),
        },
    });
};

const scrollInDirection = (direction: 'up' | 'down') => {
    if (!runPageBody.value) {
        return;
    }

    runPageBody.value.scrollBy({
        top: direction === 'up' ? -200 : 200,
        behavior: 'smooth',
    });

    const now = window.performance.now();
    if (state.lastScrollTime) {
        const diff = now - state.lastScrollTime;
        if (diff < 70) {
            runPageBody.value.scrollBy({
                top: direction === 'up' ? -50 : 50,
                behavior: 'instant',
            });
        }
    }

    state.lastScrollTime = now;
};
/* endregion Other */

/* region Lifecycle */
onMounted(() => {
    eventEmitter.addListener('hotkeyPress', onHotkeyPress);
    eventEmitter.addListener('scrollInDirection', onScrollInDirection);

    const routeParams = routeParamsSchema.parse({
        sessionId: route.params.sessionId,
        profileConfigId: route.params.profileConfigId,
        stepConfigId: route.params.stepConfigId,
        variableChainHash: route.params.variableChainHash,
    });

    const captureRevision = Number(route.query.captureRevision ?? 0);
    if (Number.isFinite(captureRevision)) {
        clientStore.screenCaptureRevisions[routeParams.sessionId] = captureRevision;
    }

    state.sessionId = routeParams.sessionId;
    state.profileConfig = ProfileConfigService.getAllProfileConfigs()[routeParams.profileConfigId];
    state.profileStepConfigs = Object.values(StepConfigService.getAllStepConfigs())
        .filter((s) => state.profileConfig?.stepIds.includes(s.id))
        .sort((a, b) => a.order - b.order);
    state.stepConfig = StepConfigService.getAllStepConfigs()[routeParams.stepConfigId];
    state.promptConfigs = Object.values(PromptConfigService.getAllPromptConfigs())
        .filter(
            (p) =>
                p.profileId === routeParams.profileConfigId &&
                p.stepId === routeParams.stepConfigId,
        )
        .sort((a, b) => a.order - b.order);
    state.variableChainHash = routeParams.variableChainHash;
});

onUnmounted(() => {
    eventEmitter.removeListener('hotkeyPress', onHotkeyPress);
    eventEmitter.removeListener('scrollInDirection', onScrollInDirection);
});
/* endregion Lifecycle */
</script>

<template>
    <div
        v-if="state.sessionId && state.stepConfig && state.profileConfig"
        class="flex-shrink flex-grow overflow-y-scroll"
        ref="runPageBody"
    >
        <NavHeader>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink as-child class="flex gap-x-1.5">
                            <RouterLink
                                :to="sessionStore.isInsideWebView ? '' : ROUTES.index"
                                :class="{ disabled: sessionStore.isInsideWebView }"
                            >
                                Home
                                <HotkeyIndicator
                                    v-if="state.stepConfig.order === 0"
                                    :hotkey="Hotkey.Left"
                                />
                            </RouterLink>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <template
                        v-for="(stepConfig, index) in state.profileStepConfigs"
                        :key="stepConfig.id"
                    >
                        <BreadcrumbSeparator v-if="index !== state.profileStepConfigs.length" />
                        <BreadcrumbItem v-if="stepConfig.id === state.stepConfig.id">
                            <BreadcrumbPage>
                                {{ stepConfig.name }}
                            </BreadcrumbPage>
                        </BreadcrumbItem>
                        <BreadcrumbItem v-else>
                            <BreadcrumbLink as-child class="flex gap-x-1.5">
                                <RouterLink
                                    to="javascript:void(0)"
                                    @click="
                                        navigateToRunPage(
                                            state.sessionId,
                                            state.profileConfig.id,
                                            stepConfig.id,
                                        )
                                    "
                                    :class="{ disabled: sessionStore.isInsideWebView }"
                                >
                                    <HotkeyIndicator
                                        v-if="state.stepConfig.order + 1 === stepConfig.order"
                                        :hotkey="Hotkey.Right"
                                    />
                                    {{ stepConfig.name }}
                                    <HotkeyIndicator
                                        v-if="state.stepConfig.order - 1 === stepConfig.order"
                                        :hotkey="Hotkey.Left"
                                    />
                                </RouterLink>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                    </template>
                </BreadcrumbList>
            </Breadcrumb>
        </NavHeader>

        <div class="px-2 pb-2">
            <template v-if="state.promptConfigs.length > 0">
                <OutputCard
                    v-for="(promptConfig, index) in state.promptConfigs"
                    :key="promptConfig.id"
                    :sessionId="state.sessionId"
                    :profileConfigId="state.profileConfig.id"
                    :stepConfigId="state.stepConfig.id"
                    :promptConfig="promptConfig"
                    :promptIndex="index"
                    :variableChainHash="state.variableChainHash"
                    :class="{ 'mb-2': index !== state.promptConfigs.length - 1 }"
                />
            </template>
            <Card v-else class="mb-2 rounded border">
                <CardHeader class="px-4 py-2 pe-2 text-lg">
                    <CardTitle>
                        <div class="flex items-center justify-between text-base">
                            Something went wrong...
                        </div>
                    </CardTitle>
                </CardHeader>
                <CardContent class="dark:bg-zinc-900/60">
                    <div class="flex items-center justify-center p-5">
                        Failed to find any valid prompt configs. Please fully reset your settings
                        and try again.
                    </div>
                </CardContent>
            </Card>
        </div>
    </div>
</template>
